/* ===========================================================================
   p1-lazy-media.js · R1C-M1-P1 — in-view hydration for below-fold media
   ---------------------------------------------------------------------------
   Build-time, selected below-fold images carry their URLs in `data-p1-src` /
   `data-p1-srcset` (a <noscript> twin keeps no-JS complete). This module
   hydrates each media group when it approaches the viewport (rootMargin
   180%), so nothing competes with the arrival — and nothing is ever missing
   when the visitor reaches it.

   Safety rails:
     · a whole <picture> hydrates atomically (sources first, then img);
     · `beforeprint` hydrates everything, so print is never incomplete;
     · anchor jumps / back-forward land inside the rootMargin and hydrate on
       the same frame the observer fires;
     · without IntersectionObserver everything hydrates immediately;
     · reduced-motion / save-data unaffected: posters are images, not motion.
   =========================================================================== */

(() => {
  if (window.__tosP1LazyMedia) return;
  window.__tosP1LazyMedia = true;

  const swapEl = (el) => {
    if (el.dataset.p1Srcset) {
      el.setAttribute("srcset", el.dataset.p1Srcset);
      delete el.dataset.p1Srcset;
    }
    if (el.dataset.p1Src) {
      el.setAttribute("src", el.dataset.p1Src);
      delete el.dataset.p1Src;
    }
  };

  const hydrateGroup = (node) => {
    const scope = node.closest("picture") ?? node;
    if (scope.tagName === "PICTURE") {
      scope.querySelectorAll("source[data-p1-srcset]").forEach(swapEl);
      scope.querySelectorAll("img[data-p1-src], img[data-p1-srcset]").forEach(swapEl);
    } else {
      swapEl(scope);
    }
  };

  const pending = [...document.querySelectorAll("img[data-p1-src], img[data-p1-srcset]")];
  if (!pending.length) return;

  const hydrateAll = () => pending.forEach(hydrateGroup);

  window.addEventListener("beforeprint", hydrateAll, { once: true });

  if (!("IntersectionObserver" in window)) {
    hydrateAll();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrateGroup(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: "180% 0px", threshold: 0 },
  );

  pending.forEach((img) => observer.observe(img.closest("picture") ?? img));
})();
